import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { fetchJourney, money } from "@/lib/marketing";

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-slate-800 text-right">{children ?? "—"}</span>
  </div>
);

const Block = ({ title, children }) => (
  <section className="mt-5">
    <h3 className="text-xs uppercase tracking-wider font-black text-slate-500 mb-1">
      {title}
    </h3>
    {children}
  </section>
);

/** Full-screen on mobile, side panel on desktop. */
export const JourneyDrawer = ({ reference, onClose }) => {
  const [j, setJ] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!reference) return;
    setJ(null);
    fetchJourney(reference).then(setJ).catch(() => setError("Journey unavailable"));
  }, [reference]);

  if (!reference) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40"
      data-testid="journey-drawer-overlay" onClick={onClose}>
      <div data-testid="journey-drawer" onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl h-full bg-white overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
              Campaign journey
            </p>
            <h2 className="text-xl font-black text-slate-900">{reference}</h2>
          </div>
          <button data-testid="journey-drawer-close" onClick={onClose}
            className="rounded-full border border-slate-300 p-2 hover:bg-slate-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && <p className="text-sm text-rose-600 mt-4">{error}</p>}
        {!j && !error && (
          <p className="flex items-center gap-2 text-sm text-slate-500 mt-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading journey…
          </p>
        )}

        {j && (
          <>
            <Block title="Acquisition">
              <Row label="First touch">
                {j.attribution.first_touch?.source || "direct"}
                {j.attribution.first_touch?.medium ? ` · ${j.attribution.first_touch.medium}` : ""}
              </Row>
              <Row label="Campaign">{j.attribution.campaign_id}</Row>
              <Row label="Creative">{j.attribution.creative || "—"}</Row>
              <Row label="Last touch">{j.attribution.last_touch?.source || "—"}</Row>
              <Row label="Assists">{(j.attribution.assists || []).length}</Row>
              <Row label="Landing visits">{j.visit?.count ?? 0}</Row>
              <Row label="Health Check start">{j.health_check_start?.occurred_at || "not tracked"}</Row>
            </Block>

            <Block title="Booking & attendance">
              <Row label="Booked">{j.booking.created_at}</Row>
              <Row label="Appointment">{j.booking.appointment_date}</Row>
              <Row label="Status">{j.booking.status}</Row>
              <Row label="Technician">{j.booking.technician}</Row>
              <Row label="Attended">{j.attendance.attended_at}</Row>
              <Row label="No show">{j.attendance.no_show_at}</Row>
              <Row label="Cancelled">{j.attendance.cancelled_at}</Row>
              <Row label="Completed">{j.completion.completed_at}</Row>
              <Row label="Recommendations">
                {(j.completion.recommendations || []).join(", ") || "—"}
              </Row>
            </Block>

            <Block title="AWARE lab">
              <Row label="Order">{j.lab.reference}</Row>
              <Row label="Package">{j.lab.package}</Row>
              <Row label="Gross">{money(j.lab.gross_revenue)}</Row>
              <Row label="Refunds">{money(j.lab.refund_amount)}</Row>
              <Row label="Net">{money(j.lab.net_revenue)}</Row>
              <Row label="Revenue status">{j.lab.revenue_status}</Row>
              <Row label="Sample">{j.lab.sample_collection_status}</Row>
            </Block>

            <Block title="Follow-ups, quotes & jobs">
              {(j.follow_ups || []).map((f) => (
                <Row key={f.id} label={f.action_type}>{f.status}</Row>
              ))}
              {(j.quotes_and_jobs || []).map((q) => (
                <Row key={q.id} label={`${q.job_state} · ${q.revenue_source}`}>
                  {money(q.net_revenue)} · {q.attribution_reason}
                </Row>
              ))}
              {!(j.follow_ups || []).length && !(j.quotes_and_jobs || []).length && (
                <p className="text-sm text-slate-500 py-2">No follow-ups or quotes yet.</p>
              )}
            </Block>

            <Block title="Revenue">
              <Row label="AWARE net lab">{money(j.revenue.aware_net_lab_revenue)}</Row>
              <Row label="AWARE-recorded PWS net">
                {money(j.revenue.pws_net_attributed_revenue)}
              </Row>
              <Row label="Combined net">{money(j.revenue.combined_net_revenue)}</Row>
            </Block>

            <Block title="Audit trail">
              <ol data-testid="journey-timeline" className="space-y-1">
                {(j.timeline || []).map((t, i) => (
                  <li key={i} className="text-xs text-slate-600">
                    <span className="font-bold text-slate-800">{t.stage}</span> · {t.at}
                    {t.detail ? ` · ${JSON.stringify(t.detail)}` : ""}
                  </li>
                ))}
              </ol>
            </Block>
          </>
        )}
      </div>
    </div>
  );
};
